import { useCallback, useContext, useLayoutEffect, useState } from 'react'
import { useCookies } from 'react-cookie'
import { type SubmitHandler, type UseFormReturn, useForm } from 'react-hook-form'
import { useHistory, useLocation } from 'react-router-dom'

import LogRocket from 'logrocket'

import { useCreateFirebaseUser } from '~/src/hooks/useCreateFirebaseUser'
import { useAttributionCookies } from '~/src/hooks/useAttributionCookies'
import { useStripeAppPayload } from '~/src/hooks/useStripeAppPayload'
import { useSalesLeadData } from '~/src/hooks/useSalesLeadData'
import { checkIdentityExists } from '~/src/utils/checkIdentityExists'
import { hasExistingBusiness } from '~/src/utils/hasExistingBusiness'
import { postSignUp } from '~/src/utils/postSignUp'
import {
  BUSINESS_EMAIL_ERROR,
  isValidEmailMessage,
  isValidEmailNoPersonalMessage,
} from '~/src/utils/emails'
import { logError, parseHttpResponseError } from '~/src/utils/errors'
import { trackEvent } from '~/src/utils/analytics'
import { HTTPError } from 'ky'
import { LeadContext, useUpdateLeadModel } from '~/src/models/leads/LeadModel'
import { PossibleInterestedProducts } from '~/src/components/UsernameSignUp'
import { SalesDemoForm } from '~/src/routes/UserQualification/pages/sales-demo-type'

export type SignUpFormFields = {
  id?: string
  first_name: string
  last_name: string
  email: string
  password: string
  interested_in_products: PossibleInterestedProducts[]
}

export type UseSignUpParams = {
  onLeadEmailMatchesExistingIdentity?: () => void
  leadEmail?: string
  /**
   * Needed for Xero requirement. We can find a better place if needed
   */
  leadFirstName?: string
  leadLastName?: string
}

export type UseSignUpResponse = {
  leadCheckForExistingIdentityIsLoading: boolean
  leadEmailMatchesExistingIdentity: boolean
  allowPersonalEmails: boolean
  form: UseFormReturn<SignUpFormFields>
  existingBusinessError: string | null
  onSubmit: SubmitHandler<SignUpFormFields>
  validateEmail: (params: { email?: string | null; allowPersonalEmails: boolean }) => string | undefined
}

const PostSignUpSchemaMismatchErrorCode = '2001' as const

export function useSignUp({
  onLeadEmailMatchesExistingIdentity,
  leadEmail,
  leadFirstName,
  leadLastName,
}: UseSignUpParams): UseSignUpResponse {
  const [isLoading, setIsLoading] = useState(false)
  const [existingBusinessError, setExistingBusinessError] = useState<string | null>(null)
  const [haveTrackedBefore, setHaveTrackedBefore] = useState(false)
  const [leadEmailMatchesExistingIdentity, setLeadEmailMatchesExistingIdentity] = useState(false)

  const history = useHistory()
  const location = useLocation<{ salesDemoFormData?: SalesDemoForm }>()
  const [cookies] = useCookies()
  useAttributionCookies()
  const createFirebaseUser = useCreateFirebaseUser()

  const stripePayload = useStripeAppPayload()
  const salesLeadData = useSalesLeadData()
  const salesDemoFormData = location.state?.salesDemoFormData

  const initialValues = getSignUpInitialValues({
    queryParams: new URLSearchParams(location.search),
    salesDemoFormData,
    salesLeadData,
    leadEmail,
    leadFirstName,
    leadLastName,
  })

  const form = useForm<SignUpFormFields>({
    defaultValues: initialValues,
    criteriaMode: 'all',
  })

  useLayoutEffect(
    () => {
      checkLeadEmailForExistingIdentity()

      async function checkLeadEmailForExistingIdentity() {
        if (!leadEmail) {
          return
        }

        try {
          setIsLoading(true)

          const identityExists = await checkIdentityExists(leadEmail)

          if (identityExists) {
            setLeadEmailMatchesExistingIdentity(true)

            onLeadEmailMatchesExistingIdentity?.()
          }
        } catch (error) {
          logError.high(error)
        } finally {
          setIsLoading(false)
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  )

  const partnerPromo = cookies.partner_promo ?? cookies.initial_partner_promo ?? cookies.promo
  const allowPersonalEmails =
    cookies.allow_personal_email_signup ||
    partnerPromo === 'smartbiz' ||
    partnerPromo === 'live-oak-bank' ||
    partnerPromo === 'grasshopper-bank' ||
    partnerPromo === 'frb' ||
    partnerPromo === 'wells-fargo' ||
    partnerPromo === 'firstbase'

  const { lead, setLead } = useContext(LeadContext)
  const updateLead = useUpdateLeadModel()

  const onSubmit = useCallback<SubmitHandler<SignUpFormFields>>(
    async (data) => {
      LogRocket.identify(data.email, { email: data.email })

      const identityExists = await checkIdentityExists(data.email)

      if (identityExists) {
        window.location.replace(`/sign-in?email=${encodeURIComponent(data.email)}`)
        return
      }

      const { valid, nextError } = await hasExistingBusiness({
        email: data.email,
        existingBusinessError,
      })
      setExistingBusinessError(nextError)

      if (valid === false) {
        return
      }

      try {
        if (lead?.id) {
          const updateLeadModelPayload = {
            interested_in_bill_payments: data.interested_in_products.includes(
              'interested_in_bill_payments'
            ),
            interested_in_corporate_cards: data.interested_in_products.includes(
              'interested_in_corporate_cards'
            ),
            interested_in_procurement: data.interested_in_products.includes('interested_in_procurement'),
            interested_in_treasury: data.interested_in_products.includes('interested_in_treasury'),
            interested_in_accounts_receivable: data.interested_in_products.includes(
              'interested_in_accounts_receivable'
            ),
            interested_in_stack: data.interested_in_products.includes('interested_in_stack'),
            interested_in_token_spend_management: data.interested_in_products.includes(
              'interested_in_token_spend_management'
            ),
          }

          try {
            const updatedLead = await updateLead(lead.id, updateLeadModelPayload)
            setLead((prevLeadModel) => {
              if (prevLeadModel) {
                return { ...prevLeadModel, ...updateLeadModelPayload, ...updatedLead }
              }

              return prevLeadModel
            })
          } catch (e) {
            logError.medium('Error updating lead onSubmit of useSignUp', {
              extra: { error: e, leadId: lead?.id },
            })
          }
        } else {
          logError.medium('No lead id found onSubmit of useSignUp')
        }

        const credentials = await createFirebaseUser({
          email: data.email,
          password: data.password,
        })

        try {
          await postSignUp({
            user: credentials.user,
            first_name: data.first_name,
            last_name: data.last_name,
            email: credentials.user.email!,
            cookies,
            stripePayload,
            salesLeadId: salesLeadData?.id,
          })
        } catch (error) {
          const { user } = credentials

          // /post-sign-up fails sometimes and it puts users in a broken state where they
          // have a Firebase user, but not a Ramp user. This ensures that when it fails,
          // we also delete the Firebase user.
          //
          // We handle three cases here: 500s in case BE fails (timeouts, bugs, network issues),
          // local request failures where browsers will throw a TypeError with various messages
          // like "load failed" or "failed to fetch", and 400s with ramp_error_code 2001.
          // That 2001 case means the `post-sign-up` request body failed backend schema validation,
          // so we still need to clean up the Firebase user created earlier in the flow.

          const handleGenericServerError = async () => {
            await user.delete()
            logError.high(new Error('Sign up new user firebase error'))
          }

          if (error instanceof TypeError && error.message?.toLowerCase().includes('failed')) {
            handleGenericServerError()
            throw error
          }

          if (error instanceof HTTPError) {
            if (error.response.status >= 500) {
              handleGenericServerError()
            } else if (error.response.status === 400) {
              const { errorCode } =
                await parseHttpResponseError<typeof PostSignUpSchemaMismatchErrorCode>(error)

              if (errorCode === PostSignUpSchemaMismatchErrorCode) {
                handleGenericServerError()
              } else {
                logError.medium('account already exists')
              }
            } else if (error.response.status === 404) {
              logError.medium('getPostSignUpToken NO_INVITE_FOUND')
            } else {
              logError.medium(new Error('getPostSignUpToken UNKNOWN_ERROR'))
            }
          }

          throw error
        }

        history.push('/verify-email', {
          email: credentials.user.email,
          sendOnLoad: true,
        })
      } catch (error) {
        const { errorMessage } = await parseHttpResponseError(error)

        if (errorMessage) {
          form.setError('email', { message: errorMessage })
        }
      }
    },
    [
      cookies,
      createFirebaseUser,
      existingBusinessError,
      form,
      history,
      salesLeadData,
      stripePayload,
      lead,
      updateLead,
      setLead,
    ]
  )

  const validateEmail = useCallback<UseSignUpResponse['validateEmail']>(
    ({ email, allowPersonalEmails }) => {
      const errorMessage = allowPersonalEmails
        ? isValidEmailMessage(email)
        : isValidEmailNoPersonalMessage(email)

      if (!email || errorMessage !== BUSINESS_EMAIL_ERROR) {
        return errorMessage
      }

      if (!haveTrackedBefore) {
        trackEvent('Personal Email Error Shown', {
          email,
          signup_method: 'RAMP_STANDARD',
        })
        setHaveTrackedBefore(true)
      }

      return `Please enter a valid business email (not ${email.split('@')[1]})`
    },
    [haveTrackedBefore]
  )

  return {
    leadCheckForExistingIdentityIsLoading: isLoading,
    leadEmailMatchesExistingIdentity,
    allowPersonalEmails,
    form,
    existingBusinessError,
    onSubmit,
    validateEmail,
  }
}

function getSignUpInitialValues({
  queryParams,
  salesDemoFormData,
  salesLeadData,
  leadEmail,
  leadFirstName,
  leadLastName,
}: {
  queryParams: URLSearchParams
  salesDemoFormData?: SalesDemoForm
  salesLeadData: ReturnType<typeof useSalesLeadData>
  leadEmail?: string
  leadFirstName?: string
  leadLastName?: string
}): Omit<SignUpFormFields, 'id'> {
  const queryFirstName = queryParams.get('first_name') ?? undefined
  const queryLastName = queryParams.get('last_name') ?? undefined
  const queryEmail = queryParams.get('email') ?? undefined
  const queryInterestedProductsString = queryParams.get('interested_in_products')
  const queryInterestedProducts = queryInterestedProductsString
    ? (queryInterestedProductsString.split(',') as PossibleInterestedProducts[])
    : []

  return {
    first_name: getFirstName({
      salesLeadFirstName: salesLeadData?.first_name,
      leadFirstName,
      salesDemoFirstName: salesDemoFormData?.first_name,
      queryFirstName,
    }),
    last_name: getLastName({
      salesLeadLastName: salesLeadData?.last_name,
      leadLastName,
      salesDemoLastName: salesDemoFormData?.last_name,
      queryLastName,
    }),
    email: getEmail({ salesLeadEmail: salesLeadData?.email, leadEmail, queryEmail }),
    password: '',
    interested_in_products: getInterestedProducts({
      salesDemoProducts: salesDemoFormData?.interested_in_products,
      queryProducts: queryInterestedProducts,
    }),
  }
}

function getFirstName({
  salesLeadFirstName,
  leadFirstName,
  salesDemoFirstName,
  queryFirstName,
}: {
  salesLeadFirstName: string | null | undefined
  leadFirstName: string | undefined
  salesDemoFirstName: string | undefined
  queryFirstName: string | undefined
}): string {
  if (salesLeadFirstName) {
    return salesLeadFirstName
  }

  if (leadFirstName) {
    return leadFirstName
  }

  if (salesDemoFirstName) {
    return salesDemoFirstName
  }

  if (queryFirstName) {
    return queryFirstName
  }

  return ''
}

function getLastName({
  salesLeadLastName,
  leadLastName,
  salesDemoLastName,
  queryLastName,
}: {
  salesLeadLastName: string | null | undefined
  leadLastName: string | undefined
  salesDemoLastName: string | undefined
  queryLastName: string | undefined
}): string {
  if (salesLeadLastName) {
    return salesLeadLastName
  }

  if (leadLastName) {
    return leadLastName
  }

  if (salesDemoLastName) {
    return salesDemoLastName
  }

  if (queryLastName) {
    return queryLastName
  }

  return ''
}

function getEmail({
  salesLeadEmail,
  leadEmail,
  queryEmail,
}: {
  salesLeadEmail: string | null | undefined
  leadEmail: string | undefined
  queryEmail: string | undefined
}): string {
  if (salesLeadEmail) {
    return salesLeadEmail
  }

  if (leadEmail) {
    return leadEmail
  }

  if (queryEmail) {
    return queryEmail
  }

  return ''
}

function getInterestedProducts({
  salesDemoProducts,
  queryProducts,
}: {
  salesDemoProducts: PossibleInterestedProducts[] | undefined
  queryProducts: PossibleInterestedProducts[]
}): PossibleInterestedProducts[] {
  if (salesDemoProducts && salesDemoProducts.length > 0) {
    return salesDemoProducts
  }

  if (queryProducts.length > 0) {
    return queryProducts
  }

  return []
}
